import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AccountForm } from "../components/accounts/AccountForm";
import { BalanceFormModal } from "../components/accounts/BalanceFormModal";
import { TimeSeriesLineChart } from "../components/charts/TimeSeriesLineChart";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { Modal } from "../components/ui/Modal";
import {
  useAccount,
  useAddBalance,
  useDeleteBalance,
  useHardDeleteAccount,
  useSoftDeleteAccount,
  useUpdateAccount,
} from "../hooks/accounts";
import { mapAccountsError } from "../lib/accounts-errors";
import { ApiError } from "../lib/api";

function formatEuroCents(cents: number): string {
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(cents / 100);
}

export function AccountDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const accountId = id ?? "";

  const account = useAccount(accountId);
  const update = useUpdateAccount(accountId);
  const softDelete = useSoftDeleteAccount();
  const hardDelete = useHardDeleteAccount();
  const addBalance = useAddBalance(accountId);
  const deleteBalance = useDeleteBalance(accountId);

  const [editOpen, setEditOpen] = useState(false);
  const [balanceOpen, setBalanceOpen] = useState(false);
  const [confirmHard, setConfirmHard] = useState(false);
  const [editErrorKey, setEditErrorKey] = useState<string | null>(null);
  const [balanceErrorKey, setBalanceErrorKey] = useState<string | null>(null);

  if (account.isLoading) {
    return (
      <div className="fv-card p-10 text-center" style={{ color: "var(--muted)" }}>
        …
      </div>
    );
  }
  if (account.isError || !account.data)
    return (
      <div
        className="fv-card p-6 text-center"
        style={{ color: "var(--neg)" }}
      >
        {t("errors.unknown")}
      </div>
    );
  const a = account.data;

  const chartData = [...a.balances]
    .sort((x, y) => x.date.localeCompare(y.date))
    .map((b) => ({ date: b.date, valueCents: b.balanceCents }));

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs mb-2" style={{ color: "var(--muted)" }}>
            <Link to="/accounts" className="hover:underline">
              {t("nav.accounts")}
            </Link>
            <span>/</span>
            <span style={{ color: "var(--text)" }}>{a.name}</span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{a.name}</h1>
          <div className="text-sm mt-1" style={{ color: "var(--muted)" }}>
            {t(`accounts.types.${a.type}`)}
            {a.institute && ` · ${a.institute}`}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            className="px-3 py-2 rounded-lg border text-sm font-medium"
            style={{ borderColor: "var(--border)", background: "var(--card)" }}
          >
            {t("accounts.detail.edit")}
          </button>
          <button
            type="button"
            onClick={() => {
              if (a.isActive) {
                softDelete.mutate(a.id, { onSuccess: () => account.refetch() });
              } else {
                update.mutate({ isActive: true }, { onSuccess: () => account.refetch() });
              }
            }}
            className="px-3 py-2 rounded-lg border text-sm font-medium"
            style={{ borderColor: "var(--border)", background: "var(--card)" }}
          >
            {a.isActive ? t("accounts.detail.deactivate") : t("accounts.detail.activate")}
          </button>
          <button
            type="button"
            disabled={a.balances.length > 0}
            title={a.balances.length > 0 ? t("accounts.detail.hardDelete.disabledHint") : undefined}
            onClick={() => setConfirmHard(true)}
            className="px-3 py-2 rounded-lg border text-sm font-medium disabled:opacity-50"
            style={{
              borderColor: "var(--border)",
              background: "var(--card)",
              color: "var(--neg)",
            }}
          >
            {t("accounts.detail.hardDelete.label")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Field label={t("accounts.detail.field.type")} value={t(`accounts.types.${a.type}`)} />
        <Field label={t("accounts.detail.field.institute")} value={a.institute ?? "—"} />
        <Field
          label={t("accounts.detail.field.liquidity")}
          value={t(`accounts.liquidity.${a.liquidityCategory}`)}
        />
        <Field label={t("accounts.detail.field.sector")} value={t(`accounts.sector.${a.sector}`)} />
        <Field
          label={t("accounts.detail.field.currentBalance")}
          value={a.latestBalance ? formatEuroCents(a.latestBalance.balanceCents) : "—"}
        />
        <Field
          label={t("accounts.detail.field.status")}
          value={a.isActive ? t("accounts.detail.active") : t("accounts.detail.inactive")}
        />
      </div>

      <div className="bg-white border border-slate-200 rounded p-4">
        <h2 className="font-semibold mb-2">{t("accounts.detail.chart")}</h2>
        <TimeSeriesLineChart data={chartData} />
      </div>

      <div className="bg-white border border-slate-200 rounded p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold">{t("accounts.detail.history")}</h2>
          <button
            type="button"
            onClick={() => setBalanceOpen(true)}
            className="px-3 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded text-sm"
          >
            {t("accounts.detail.addBalance")}
          </button>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-slate-500 text-left">
              <th className="py-1">{t("accounts.detail.column.date")}</th>
              <th className="py-1 text-right">{t("accounts.detail.column.amount")}</th>
              <th className="py-1">{t("accounts.detail.column.source")}</th>
              <th className="py-1">{t("accounts.detail.column.note")}</th>
              <th className="py-1">&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {a.balances.map((b) => (
              <tr key={b.id} className="border-t border-slate-200">
                <td className="py-1">{b.date}</td>
                <td className="py-1 text-right">{formatEuroCents(b.balanceCents)}</td>
                <td className="py-1">{t(`accounts.balance.source.${b.source}`)}</td>
                <td className="py-1">{b.note ?? ""}</td>
                <td className="py-1 text-right">
                  <button
                    type="button"
                    onClick={() =>
                      deleteBalance.mutate(b.id, { onSuccess: () => account.refetch() })
                    }
                    className="text-red-600 hover:underline"
                  >
                    {t("accounts.detail.deleteBalance")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={editOpen} onClose={() => setEditOpen(false)} title={t("accounts.detail.edit")}>
        <AccountForm
          mode="edit"
          initial={{
            name: a.name,
            type: a.type,
            institute: a.institute ?? undefined,
            liquidityCategory: a.liquidityCategory,
            sector: a.sector,
            notes: a.notes ?? undefined,
          }}
          submitting={update.isPending}
          errorKey={editErrorKey}
          onSubmit={(input) => {
            setEditErrorKey(null);
            update.mutate(
              {
                name: input.name,
                type: input.type,
                institute: input.institute ?? null,
                liquidityCategory: input.liquidityCategory,
                sector: input.sector,
                notes: input.notes ?? null,
              },
              {
                onSuccess: () => {
                  setEditOpen(false);
                  account.refetch();
                },
                onError: (err) => {
                  if (err instanceof ApiError) setEditErrorKey(mapAccountsError(err.code));
                  else setEditErrorKey("errors.unknown");
                },
              },
            );
          }}
          onCancel={() => setEditOpen(false)}
        />
      </Modal>

      <BalanceFormModal
        open={balanceOpen}
        onClose={() => setBalanceOpen(false)}
        existingBalances={a.balances}
        submitting={addBalance.isPending}
        errorKey={balanceErrorKey}
        onSubmit={(input) => {
          setBalanceErrorKey(null);
          addBalance.mutate(input, {
            onSuccess: () => {
              setBalanceOpen(false);
              account.refetch();
            },
            onError: (err) => {
              if (err instanceof ApiError) setBalanceErrorKey(mapAccountsError(err.code));
              else setBalanceErrorKey("errors.unknown");
            },
          });
        }}
        onReplace={(oldId, input) => {
          setBalanceErrorKey(null);
          deleteBalance.mutate(oldId, {
            onSuccess: () => {
              addBalance.mutate(input, {
                onSuccess: () => {
                  setBalanceOpen(false);
                  account.refetch();
                },
              });
            },
          });
        }}
      />

      <ConfirmDialog
        open={confirmHard}
        title={t("accounts.detail.hardDelete.title")}
        message={t("accounts.detail.hardDelete.message", { name: a.name })}
        confirmLabel={t("accounts.detail.hardDelete.confirm")}
        destructive
        onCancel={() => setConfirmHard(false)}
        onConfirm={() => {
          hardDelete.mutate(a.id, {
            onSuccess: () => navigate("/accounts"),
            onError: () => setConfirmHard(false),
          });
        }}
      />
    </section>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white border border-slate-200 rounded p-3">
      <p className="text-xs uppercase text-slate-500">{label}</p>
      <p className="mt-1">{value}</p>
    </div>
  );
}
